"use client";

import { useMemo, useState } from "react";
import {
  FONT_MONO,
  TEXT_MUTE,
} from "@/components/ui/organisms/chord-practice/palette";
import type { DifficultyMeasure } from "@/lib/music/analyzer-types";

interface DifficultyHeatmapProps {
  measures: ReadonlyArray<DifficultyMeasure>;
  onSelectMeasure: (measure: number) => void;
}

type Bucket = {
  /** Aria-friendly label, e.g. "Mesure 5" or "Mesures 5–8". */
  label: string;
  /** First measure included in the bucket, used as click target. */
  firstMeasure: number;
  /** Aggregated normalized score in [0, 1]. */
  normalized: number;
};

const MAX_BARS = 64;

/** Multi-stop gradient: cool blue → violet → warm → gold. */
const COLOR_STOPS: ReadonlyArray<readonly [number, readonly [number, number, number]]> = [
  [0, [40, 50, 80]],
  [0.35, [80, 70, 130]],
  [0.65, [180, 120, 90]],
  [1, [251, 191, 36]],
];

/**
 * Difficulty horizon at the bottom of the overlay : one bar per
 * measure (or aggregated bucket beyond 240 measures), height
 * proportional to difficulty, color interpolated through a 4-stop
 * gradient (cool → warm). The piece stays minimal, clicking a bar
 * scrolls the studio to that measure and closes the overlay.
 *
 * Bright peaks above 0.85 emit a soft glow so dramatic passages stand
 * out without help text or tooltips. No captions / legends, the
 * shape and palette carry the meaning.
 */
export function DifficultyHeatmap({
  measures,
  onSelectMeasure,
}: DifficultyHeatmapProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const buckets = useMemo(() => computeBuckets(measures), [measures]);

  if (buckets.length === 0) {
    return null;
  }

  return (
    <div style={{ padding: "0 40px" }}>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 9,
          color: TEXT_MUTE,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          marginBottom: 8,
        }}
      >
        Difficulté par mesure
      </div>
      {/*
        La hauteur passe par une classe et non par le style inline du reste du
        fichier : c'est le seul moyen de la faire varier sans hook de media
        query, qui annoncerait la version mobile au premier rendu et ferait
        clignoter le desktop. `h-24` reprend exactement les 96 px d'origine.

        96 px de barres, plus le titre du morceau et ses statistiques, ne
        tiennent pas dans les ~250 px que laisse un téléphone couché : le
        graphe se faisait couper par le bas. Le profil de difficulté reste
        lisible à 56 px — c'est une silhouette qu'on lit, pas des valeurs
        qu'on relève.
      */}
      <div
        className="h-24 [@media(max-height:480px)]:h-14"
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
        }}
      >
        {buckets.map((bucket, idx) => (
          <Bar
            key={`${bucket.firstMeasure}-${idx}`}
            bucket={bucket}
            hovered={hoverIdx === idx}
            onEnter={() => setHoverIdx(idx)}
            onLeave={() => setHoverIdx(null)}
            onClick={() => onSelectMeasure(bucket.firstMeasure)}
          />
        ))}
      </div>
    </div>
  );
}

function Bar({
  bucket,
  hovered,
  onEnter,
  onLeave,
  onClick,
}: {
  bucket: Bucket;
  hovered: boolean;
  onEnter: () => void;
  onLeave: () => void;
  onClick: () => void;
}) {
  const top = lerpColor(bucket.normalized);
  const bottom = lerpColor(bucket.normalized * 0.5);
  const heightPct = Math.max(8, bucket.normalized * 100);
  const opacity = hovered
    ? 1
    : Math.max(0.45, bucket.normalized * 0.95 + 0.1);
  const glow = bucket.normalized > 0.85 ? `0 0 12px ${top}` : "none";

  return (
    <button
      type="button"
      aria-label={`${bucket.label}, difficulté ${Math.round(bucket.normalized * 10)} sur 10`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      onClick={onClick}
      style={{
        flex: 1,
        height: `${heightPct}%`,
        background: `linear-gradient(180deg, ${top}, ${bottom})`,
        borderRadius: "1.5px 1.5px 0 0",
        border: "none",
        padding: 0,
        outline: "none",
        cursor: "pointer",
        opacity,
        boxShadow: hovered ? `0 0 14px ${top}` : glow,
        transition: "opacity 120ms, box-shadow 120ms, transform 120ms",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
      }}
    />
  );
}

// ── Bucketing & color math ─────────────────────────────────────────

function computeBuckets(
  measures: ReadonlyArray<DifficultyMeasure>,
): Bucket[] {
  const count = measures.length;

  if (count === 0) {
    return [];
  }

  const minScore = Math.min(...measures.map((m) => m.score));
  const maxScore = Math.max(...measures.map((m) => m.score));
  const range = maxScore - minScore;

  const normalize = (score: number): number => {
    if (range <= 0) {
      return 0.5;
    }

    return Math.max(0, Math.min(1, (score - minScore) / range));
  };

  // Cap the number of bars so the heatmap stays readable and each bar stays clickable.
  const groupSize = Math.max(1, Math.ceil(count / MAX_BARS));
  const buckets: Bucket[] = [];

  for (let i = 0; i < count; i += groupSize) {
    const slice = measures.slice(i, i + groupSize);

    if (slice.length === 0) {
      continue;
    }

    const firstMeasure = slice[0].measure;
    const lastMeasure = slice[slice.length - 1].measure;
    const score = Math.max(...slice.map((m) => m.score));
    const label =
      slice.length === 1
        ? `Mesure ${firstMeasure}`
        : `Mesures ${firstMeasure}–${lastMeasure}`;

    buckets.push({
      label,
      firstMeasure,
      normalized: normalize(score),
    });
  }

  return buckets;
}

function lerpColor(t: number): string {
  const clamped = Math.max(0, Math.min(1, t));

  let lo = COLOR_STOPS[0];
  let hi = COLOR_STOPS[COLOR_STOPS.length - 1];

  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    const a = COLOR_STOPS[i];
    const b = COLOR_STOPS[i + 1];

    if (clamped >= a[0] && clamped <= b[0]) {
      lo = a;
      hi = b;
      break;
    }
  }

  const span = hi[0] - lo[0];
  const localT = span > 0 ? (clamped - lo[0]) / span : 0;
  const r = Math.round(lo[1][0] + (hi[1][0] - lo[1][0]) * localT);
  const g = Math.round(lo[1][1] + (hi[1][1] - lo[1][1]) * localT);
  const b = Math.round(lo[1][2] + (hi[1][2] - lo[1][2]) * localT);

  return `rgb(${r}, ${g}, ${b})`;
}
